import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import { subscriptionPlanApi } from '../../services/subscriberApi.js';
import {
  durationShortLabel,
  formatPkrAmount,
  maxPlanSavingsPct,
  planAvgPerMonth,
  planSavingsPct,
} from '../../utils/planPricing.js';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const STEPS = [
  {
    no: '01',
    title: 'Browse Issues',
    tagline: 'Explore Every Edition',
    description: 'Visit our shop or magazines page to see every published Flare issue. Tap any cover for a full preview, inside contents, and reader reviews before you buy.',
    image: '/how-it-works/1.png',
    bullets: [
      'High-resolution cover previews',
      'Full inside contents & editorial list',
      'See free vs paid sections instantly',
    ],
  },
  {
    no: '02',
    title: 'Choose Your Format',
    tagline: 'Digital PDF or Printed Copy?',
    description: 'Every Flare issue comes in two flexible formats. Pick instant digital download for seconds-to-read access or premium printed copy for that luxurious feel — or get both at a combo discount.',
    image: '/how-it-works/2.png',
    bullets: [
      'Digital PDF: Free or low-cost • instant',
      'Printed: Matte-laminated • A4 size • courier delivery',
      'Combo bundle: Save up to 30%',
    ],
  },
  {
    no: '03',
    title: 'Secure Checkout',
    tagline: 'Pakistan’s Most Trusted Ways to Pay',
    description: 'Pay however you like — all transactions are 100% encrypted and backed by secure Pakistani gateways. You get instant email & SMS confirmation the second your payment clears.',
    image: '/how-it-works/3.png',
    bullets: [
      'JazzCash & EasyPaisa (mobile wallets)',
      'Direct bank transfer • Debit/Credit cards',
      'JazzCash, EasyPaisa, bank transfer, or card (COD coming later)',
    ],
  },
  {
    no: '04',
    title: 'Enjoy Reading',
    tagline: 'Your Copy — In Seconds or At Your Door',
    description: 'Digital buyers get instant download links via email + account dashboard (save forever, no expiry). Printed copies are dispatched via trusted courier within 24 to 72 hours all across Pakistan.',
    image: '/how-it-works/4.png',
    bullets: [
      'Digital: PDF download in email & dashboard',
      'Printed: Courier tracking number via SMS',
      'Free re-downloads for lifetime',
    ],
  },
];

const FAQS = [
  { q: 'How quickly do I get my digital PDF?', a: 'Instantly. The second your payment is confirmed you see the download button on-screen AND receive an email. No waiting ever.' },
  { q: 'Which cities do you deliver printed copies to?', a: 'All major cities in Pakistan: Lahore, Karachi, Islamabad, Rawalpindi, Faisalabad, Multan, Peshawar, Quetta, Hyderabad, Gujranwala, Sialkot & 200+ smaller cities and towns via TCS, Leopard & Pakistan Post.' },
  { q: 'What does a yearly subscription include?', a: '12 automatic issues with free delivery every month, exclusive subscriber-only articles, priority printing, early access to cover reveals & invites to Flare community events.' },
  { q: 'Can I cancel my subscription?', a: 'Yes anytime — no penalties. You keep all issues already paid for. Unused months are fully refunded on request (within 7 days of renewal).' },
  { q: 'Do single issues expire?', a: 'Never. Once you buy any issue it lives in your account dashboard forever. Re-download as many times as you need, on any device.' },
];

function ShopHowItWorksSection() {
  const [openFaq, setOpenFaq] = useState(0);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await subscriptionPlanApi.getActive();
        const list = r?.data?.subscription_plans || r?.data?.plans || [];
        if (!cancelled && Array.isArray(list)) setPlans(list);
      } catch {
        /* keep empty */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const maxSave = maxPlanSavingsPct(plans) || 40;
  const yearly = plans.find((p) => Number(p.duration_months) === 12);
  const biennial = plans.find((p) => Number(p.duration_months) === 24);
  const preferred = [1, 3, 12, 24];
  const byMonth = new Map(plans.map((p) => [Number(p.duration_months), p]));
  const offerPlans = preferred.map((m) => byMonth.get(m)).filter(Boolean);
  const cards = (offerPlans.length ? offerPlans : plans).slice(0, 4);

  return (
    <ShopLayout>
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #fff5f5 0%, #ffffff 55%, #fff5f5 100%)' }}>
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(196,21,28,0.18), transparent 70%)' }} />
        <div className="pointer-events-none absolute top-40 -right-20 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.14), transparent 70%)' }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest bg-white shadow-sm border border-gray-100 mb-6" style={{ color: FLARE_RED }}>
            <Icon path="M13 10V3L4 14h7v7l9-11h-7z" className="h-3.5 w-3.5" />
            How It Works
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-gray-900 mb-5">
            Get Flare Magazine in{' '}
            <span className="relative inline-block">
              <span style={{ color: FLARE_RED }}>4 simple steps</span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none"><path d="M3 9C80 1 220 1 297 9" stroke={FLARE_RED} strokeWidth="4" strokeLinecap="round" opacity="0.25" /></svg>
            </span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-gray-600 font-medium leading-relaxed">
            From browsing the latest issue to holding the printed copy in your hands — the whole process takes less than a minute (or a few days for delivery, your choice).
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/magazines" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl text-white font-black shadow-lg hover:shadow-xl transition-shadow" style={{ backgroundColor: FLARE_RED, boxShadow: '0 10px 30px -10px rgba(196,21,28,0.45)' }}>
              <Icon path="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" className="h-5 w-5" />
              Browse Latest Issues
            </Link>
            <Link to="/plans" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white border-2 border-gray-200 text-gray-900 font-black shadow-sm hover:border-gray-300 hover:shadow-md transition-all">
              <Icon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-5 w-5" style={{ color: FLARE_RED }} />
              View Subscription Plans
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { n: '< 1 min', l: 'To checkout' },
              { n: '0 sec', l: 'Digital delivery' },
              { n: '24–72h', l: 'Printed delivery' },
              { n: '24/7', l: 'Live support' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                <div className="text-2xl font-black mb-1" style={{ color: FLARE_RED }}>{s.n}</div>
                <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-32 bottom-20 w-[2px] -translate-x-1/2" style={{ background: 'linear-gradient(180deg, rgba(196,21,28,0.25), rgba(79,70,229,0.25) 50%, rgba(196,21,28,0.25))' }} />
          <div className="space-y-12 lg:space-y-20">
            {STEPS.map((step, i) => {
              const reverse = (i % 2 === 1);
              return (
                <div key={step.no} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4" style={{ backgroundColor: 'rgba(196,21,28,0.08)', color: FLARE_RED }}>
                      Step {step.no} • {step.tagline}
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 mb-5 leading-tight">
                      {step.title}
                    </h2>
                    <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-6 font-medium">
                      {step.description}
                    </p>
                    <ul className="space-y-3">
                      {step.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-emerald-50 border border-emerald-200">
                            <Icon path="M5 13l4 4L19 7" className="h-3.5 w-3.5 text-emerald-600" />
                          </div>
                          <span className="font-semibold text-gray-700">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative flex items-center justify-center">
                    <div className="absolute inset-0 rounded-3xl opacity-70" style={{ background: `radial-gradient(circle at 30% 30%, rgba(196,21,28,0.12), transparent 60%), radial-gradient(circle at 70% 70%, rgba(79,70,229,0.10), transparent 60%)` }} />
                    <div className="relative w-full max-w-md aspect-square bg-[#fbf7f2] rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute top-5 left-5 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg z-10" style={{ backgroundColor: FLARE_RED }}>
                        <span className="text-white font-black text-xl">{step.no}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest bg-white shadow-sm border border-gray-100 mb-5" style={{ color: FLARE_RED }}>
            <Icon path="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3.5 w-3.5" />
            FAQ
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-gray-900 mb-3">Frequently Asked Questions</h2>
          <p className="text-gray-600 font-medium max-w-2xl mx-auto">Can’t find your answer? Message our team from the <Link to="/contact" className="font-bold underline underline-offset-4" style={{ color: FLARE_RED }}>Contact page</Link>.</p>
        </div>
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {FAQS.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={i} className="transition-colors" style={isOpen ? { backgroundColor: 'rgba(196,21,28,0.025)' } : {}}>
                <button
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 sm:px-8 py-5 sm:py-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-white" style={{ backgroundColor: isOpen ? FLARE_RED : '#e5e7eb', color: isOpen ? '#fff' : '#374151' }}>{i + 1}</span>
                    <span className="font-black text-gray-900 text-base sm:text-lg">{f.q}</span>
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-200 bg-white shadow-sm">
                    <Icon path={isOpen ? 'M20 12H4' : 'M12 4v16m8-8H4'} className="h-4 w-4" style={{ color: FLARE_RED }} />
                  </div>
                </button>
                {isOpen && (
                  <div className="px-6 sm:px-8 pb-6 sm:pb-7 pl-10 sm:pl-20 pr-10 sm:pr-12">
                    <p className="text-gray-600 leading-relaxed font-medium">{f.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="mb-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl p-10 sm:p-16 text-white" style={{ background: `linear-gradient(135deg, ${FLARE_RED} 0%, #ef4444 50%, ${FLARE_RED_DARK} 100%)` }}>
          <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.15), transparent 55%), radial-gradient(circle at 85% 85%, rgba(255,255,255,0.18), transparent 50%)' }} />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-xs font-black uppercase tracking-widest mb-5">
                <Icon path="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3.5 w-3.5" />
                Limited Time Offer
              </div>
              <h3 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 leading-tight">
                Save up to {maxSave}% on Printed Flare
              </h3>
              <p className="text-white/85 font-medium leading-relaxed mb-8 max-w-lg">
                {yearly
                  ? `Yearly averages ${formatPkrAmount(planAvgPerMonth(yearly))} / month (${planSavingsPct(yearly)}% off).`
                  : ''}
                {biennial
                  ? ` Biennial averages ${formatPkrAmount(planAvgPerMonth(biennial))} / month (${planSavingsPct(biennial)}% off).`
                  : ''}
                {' '}Every issue delivered automatically, free courier across Pakistan.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/plans" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white font-black shadow-xl" style={{ color: FLARE_RED }}>
                  <Icon path="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-5 w-5" />
                  See All Plans
                </Link>
                <Link to="/magazines" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/10 border-2 border-white/40 backdrop-blur-sm font-black hover:bg-white/20 transition-colors">
                  <Icon path="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" className="h-5 w-5" />
                  Shop Issues
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-md lg:ml-auto">
              {cards.map((plan) => {
                const months = Number(plan.duration_months) || 1;
                const pct = planSavingsPct(plan);
                const best = months === 12;
                const label = pct > 0
                  ? `${durationShortLabel(months)} · Save ${pct}%`
                  : durationShortLabel(months);
                return (
                <div key={plan.id} className={`relative rounded-2xl p-4 sm:p-5 ${best ? 'bg-white text-gray-900 shadow-2xl' : 'bg-white/10 backdrop-blur-sm border border-white/20'}`}>
                  {best && (
                    <div className="absolute -top-2.5 left-4 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow" style={{ backgroundColor: '#111827' }}>
                      Most Popular
                    </div>
                  )}
                  <div className={`text-[10px] font-black uppercase tracking-widest mb-1.5 ${best ? 'text-gray-500' : 'text-white/80'}`}>{label}</div>
                  <div className={`text-2xl sm:text-3xl font-black ${best ? '' : 'text-white'}`} style={best ? { color: FLARE_RED } : {}}>{formatPkrAmount(plan.price_pkr)}</div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </ShopLayout>
  );
}

export default ShopHowItWorksSection;
